import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { faBuildingCircleArrowRight, faCirclePlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap-5';

import { fetchOwners, fetchRemainingCredits, syncToRethink } from '../../../helpers/prospect_api';
import { setOwners } from 'bundles/connect/components/store/actions/owners';
import { displayToast } from 'bundles/connect/helpers/display_toast';
import IncludeOwnerDataCheckbox from './include_owner_data_checkbox';
import type { OwnershipsResponse } from 'bundles/connect/types';

type AddPropertyToRethinkProps = {
  asIcon?: boolean;
  insightsPropertyId: string;
  lastFetched: Date | null;
  propertySyncedAt?: string;
  setOwners: (response: OwnershipsResponse) => void;
}

function AddPropertyToRethink({
  asIcon = false,
  insightsPropertyId,
  lastFetched,
  propertySyncedAt,
  setOwners
}: AddPropertyToRethinkProps) {
  const [show, setShow] = useState(false);
  const [saveContact, setSaveContact] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncTime, setSyncTime] = useState(propertySyncedAt);
  const [outOfCredits, setOutOfCredits] = useState<boolean | undefined>(undefined);

  const triggerRef = useRef(null);
  const cannotIncludeContacts = outOfCredits && !lastFetched;

  const handleModalDismiss = () => {
    setShow(false);
    setSaveContact(false);
    setLoading(false);
  };

  const rethinkSync = async (includeContacts?: boolean) => {
    await syncToRethink({
      insights_property_id: insightsPropertyId,
      include_contacts: includeContacts
    }).then((res) => {
      if (res.property.rethinkId) {
        displayToast('Successfully Saved Property', 'success');
        setSyncTime(new Date().toLocaleString());
      } else {
        displayToast('Property Already Exists', 'error');
      }
      handleModalDismiss();
    });
  };

  const handleSubmit = useCallback(() => {
    setSaving(true);
    if (saveContact) {
      fetchOwners(insightsPropertyId, !lastFetched, false)
        .then((results) => {
          setOwners(results);
          rethinkSync(true);
        })
        .catch(() => {
          displayToast('Something went wrong, please try again', 'error');
          setSaving(false);
        });
    } else {
      rethinkSync();
    }
  }, [insightsPropertyId, saveContact]);

  useEffect(() => {
    if (show) {
      setOutOfCredits(undefined);
      setLoading(true);
      fetchRemainingCredits()
        .then((res) => {
          setOutOfCredits(res.remainingCredits <= 0);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [show]);

  if (syncTime && !asIcon) {
    return <div className="text-muted small">Added at {syncTime}</div>;
  }

  const trigger = (
    <div className="align-content-center text-center h-100" ref={triggerRef} >
      <FontAwesomeIcon
        className="text-primary clickable"
        icon={faBuildingCircleArrowRight}
        onClick={() => setShow(true)}
      />
    </div>
  );

  const renderTooltip = (
    <Tooltip id={`add-to-list-tooltip-${insightsPropertyId}`}>
      Add Property
    </Tooltip>
  );

  return (
    <>
      <Modal show={show} onHide={handleModalDismiss}>
        <Modal.Header>
          <Modal.Title>
            Add Property
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <IncludeOwnerDataCheckbox
              cannotIncludeContacts={cannotIncludeContacts}
              checked={saveContact}
              disabled={saving || cannotIncludeContacts}
              onChange={() => setSaveContact(!saveContact)}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={saving || loading} variant="secondary" onClick={() => handleSubmit()}>
            Save Property
            {saving && (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm"/>
              </div>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {asIcon ?
        (
          <OverlayTrigger
            overlay={renderTooltip}
            placement="right"
            popperConfig={{
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 8],
                  },
                }
              ]
            }}
          >
            {trigger}
          </OverlayTrigger>
        ) :
        (
          <Button variant="text-primary" onClick={() => setShow(true)}>
            <FontAwesomeIcon icon={faCirclePlus} />
            Add as Property
          </Button>
        )}
    </>
  );
}

export default connect(undefined, { setOwners })(AddPropertyToRethink);
